/**
 * composables/usePeople.ts
 *
 * Composable for accessing and managing person data
 */

import { useCurrentUser } from 'vuefire'
import { useDatabase } from '@/composables/useDatabase'
import { peopleGetBatchGet } from '@/services/googleApi'
import { useUIStore } from '@/stores/ui'
import type { ActorId, GCommentDataAuthor, GPeopleApiPerson, GPerson } from '@/types/google'
import { KNOWN_ACTOR_IDS } from '@/types/google'
import type { User } from 'firebase/auth'

// how long to consider GPerson.commentAuthor data "fresh" before re-augmenting it from a new comment
const COMMENT_AUTHOR_REFRESH_INTERVAL = 60 * 60 * 1000 // 1 hour

// Google avatar-style background colors, all with good contrast for white initials text
const AVATAR_BG_COLORS = [
    '#F44336', // red
    '#E91E63', // pink
    '#9C27B0', // purple
    '#673AB7', // deep purple
    '#3F51B5', // indigo
    '#1E88E5', // blue
    '#009688', // teal
    '#43A047', // green
    '#FB8C00', // orange
    '#6D4C41', // brown
]

const SCREENSHOTS = import.meta.env.VITE_SCREENSHOTS === 'true'

function anonymizeEmail (email?: string): string | undefined {
    return email?.replace(/@.*$/, '@domain.com')
}

function resizePhoto (photo?: string): string | undefined {
    return photo?.replace(/=s100$/, '=s256')
}

/**
 * When VITE_SCREENSHOTS is enabled, mask email domains and enlarge photos before saving to Dexie
 */
function applyScreenshotsMode (person: GPerson): GPerson {
    if (!SCREENSHOTS) {
        return person
    }

    person.email = anonymizeEmail(person.email)
    person.photo = resizePhoto(person.photo)

    if (person.peopleApiPerson) {
        person.peopleApiPerson.email = anonymizeEmail(person.peopleApiPerson.email)
        person.peopleApiPerson.photo = resizePhoto(person.peopleApiPerson.photo)
    }

    if (person.commentAuthor) {
        person.commentAuthor.email = anonymizeEmail(person.commentAuthor.email)
        person.commentAuthor.photo = resizePhoto(person.commentAuthor.photo)
    }

    return person
}

/**
 * Pick a random background color (from a fixed palette) to use behind a person's initials avatar
 */
function getRandomAvatarBgColor (): string {
    return AVATAR_BG_COLORS[Math.floor(Math.random() * AVATAR_BG_COLORS.length)]!
}

/**
 * Generate initials from name components, display name, or email address (in that priority order)
 */
function generateInitials (givenName?: string, familyName?: string, displayName?: string, emailAddress?: string): string {
    if (givenName && familyName) {
        return `${givenName[0]}${familyName[0]}`.toUpperCase()
    }

    if (displayName) {
        const parts = displayName.split(' ')
        return parts.length >= 2 && parts[0]?.[0] && parts[1]?.[0]
            ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
            : displayName.slice(0, 2).toUpperCase()
    }

    if (emailAddress) {
        const localPart = emailAddress.split('@', 1)[0] ?? ''
        const parts = localPart.split(/[._-]/).filter(Boolean)
        return parts.length >= 2 && parts[0]?.[0] && parts[1]?.[0]
            ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
            : localPart.slice(0, 2).toUpperCase()
    }

    return ''
}

/**
 * Get a predefined GPerson for known actor IDs
 */
function getKnownPerson (id: ActorId): GPerson {
    if (id === 'ME') {
        const user = useCurrentUser().value

        if (user) {
            return getMe(user)
        }
    }

    return {
        personId: id,
        displayName: '',
        initials: id.slice(0, 2).toUpperCase(),
        useInitialsAsAvatar: true,
        bgColor: '#CCCCCC', // default gray background for known actors
    }
}

/**
 * Split a display name into given name and family name (first/last word)
 */
function splitDisplayName (displayName?: string): { givenName?: string, familyName?: string } {
    if (!displayName) {
        return {}
    }

    const parts = displayName.trim().split(/\s+/)
    return {
        givenName: parts[0],
        familyName: parts.length >= 2 ? parts.at(-1) : undefined,
    }
}

/**
 * Get the GPerson representing the currently logged in user, from the Firebase auth user
 */
function getMe (user: User): GPerson {
    const displayName = user.displayName ?? undefined
    const photo = user.photoURL ?? undefined
    const emailAddress = user.email ?? undefined
    const { givenName, familyName } = splitDisplayName(displayName)
    const initials = generateInitials(givenName, familyName, displayName, emailAddress) || 'ME'

    return {
        personId: 'ME',
        displayName,
        givenName,
        familyName,
        initials,
        email: emailAddress,
        photo,
        useInitialsAsAvatar: !photo,
        bgColor: '#CCCCCC', // default gray background for the current user
    }
}

function isKnownActorId (id: ActorId): id is typeof KNOWN_ACTOR_IDS[number] {
    return (KNOWN_ACTOR_IDS as readonly string[]).includes(id)
}

/**
 * Composable for managing person data
 */
export function usePeople () {
    /**
     * Get multiple persons by their actor IDs
     * Handles known actors separately and fetches remaining ones from the database
     *
     * @param ids - Array of actor IDs to look up
     * @returns Map of ActorId to GPerson objects
     */
    async function fetchAllPersons (): Promise<Map<ActorId, GPerson>> { // ids: ActorId[]
        const db = useDatabase()

        // Extract known IDs
        // const knownIds = ids.filter(id => isKnownActorId(id))
        // const peopleIds = ids.filter(id => !isKnownActorId(id))

        // Get persons from database for remaining IDs

        // const dbPersons = peopleIds.length > 0
        //     ? await db.people.where('personId').anyOf(peopleIds).toArray()
        //     : []

        const dbPersons = await db.people.toArray()

        // Create a Map with personId as key
        const personsMap = new Map<ActorId, GPerson>()

        // Add known persons
        for (const id of KNOWN_ACTOR_IDS) {
            personsMap.set(id, getKnownPerson(id))
        }

        // Create a set of person IDs found in database for quick lookup
        // const dbPersonIds = new Set(dbPersons.map(person => person.personId))

        // Add database persons
        for (const person of dbPersons) {
            personsMap.set(person.personId, person)
        }

        // Add placeholder persons for IDs not found in database
        // for (const id of peopleIds) {
        //     if (!dbPersonIds.has(id)) {
        //         personsMap.set(id, getPlaceholderPerson(id))
        //     }
        // }

        return personsMap
    }

    async function fetchNewPeople (ids: ActorId[]): Promise<number> {
        const db = useDatabase()
        const ary = ids.filter(id => !isKnownActorId(id))

        if (ary.length === 0) {
            return 0
        }

        const data = await db.people.where('personId').anyOf(ary).toArray() // .primaryKeys() could be used if only keys are needed
        const existingKeys = data.map(person => person.personId)
        const existingKeysSet = new Set(existingKeys)

        // Find IDs that are NOT in the database (new people to fetch)
        const newIds = ary.filter(id => !existingKeysSet.has(id))
        if (newIds.length === 0) {
            return 0
        }

        // Fetch people data from Google People API
        const response = await peopleGetBatchGet(newIds)
        const persons = extractPersons(response)

        // Save to database
        if (persons.length > 0) {
            await db.people.bulkAdd(persons.map(applyScreenshotsMode))
        }

        return persons.length
    }

    /**
     * Extract display name, given name, and family name from person data
     */
    function extractDisplayName (personData: gapi.client.people.Person) {
        const name = personData.names?.[0]
        return {
            displayName: name?.displayName,
            givenName: name?.givenName,
            familyName: name?.familyName,
        }
    }

    /**
     * Extract email address from person data
     */
    function extractEmail (personData: gapi.client.people.Person): string | undefined {
        return personData.emailAddresses?.[0]?.value
    }

    /**
     * Extract the photo URL from person data, preferring a custom user-uploaded photo.
     *
     * A custom photo has no `default` key at all (a `false` boolean is omitted by the API).
     * If every photo is a default placeholder, prefer the one sourced from a CONTACT record.
     */
    function extractPhoto (personData: gapi.client.people.Person): { photo?: string, isDefaultPhoto: boolean } {
        const photos = personData.photos ?? []

        const customPhoto = photos.find(photo => !photo.default)
        if (customPhoto?.url) {
            return { photo: customPhoto.url, isDefaultPhoto: false }
        }

        const contactPhoto = photos.find(photo => photo.metadata?.source?.type === 'CONTACT')
        return { photo: (contactPhoto ?? photos[0])?.url, isDefaultPhoto: true }
    }

    /**
     * Extract a GPeopleApiPerson (name/email/photo) from a Google People API person record
     */
    function extractPeopleApiPerson (personData: gapi.client.people.Person): GPeopleApiPerson {
        const { displayName, givenName, familyName } = extractDisplayName(personData)
        const email = extractEmail(personData)
        const { photo, isDefaultPhoto } = extractPhoto(personData)

        return {
            displayName,
            givenName,
            familyName,
            email,
            photo,
            isDefaultPhoto,
            updated: Date.now(),
        }
    }

    /**
     * Recalculate GPerson's display fields (name, initials, email, photo, useInitialsAsAvatar)
     * from its `peopleApiPerson` and `commentAuthor` data. `peopleApiPerson` takes priority,
     * falling back to `commentAuthor` for any missing field; the comment author's photo is used
     * instead of the People API one when the latter is only a default placeholder photo.
     */
    function updateProfile (person: GPerson): void {
        const api = person.peopleApiPerson
        const comment = person.commentAuthor

        const displayName = api?.displayName ?? comment?.displayName
        const givenName = api?.givenName ?? comment?.givenName
        const familyName = api?.familyName ?? comment?.familyName
        const email = api?.email ?? comment?.email
        const photo = (api?.photo && !api.isDefaultPhoto) ? api.photo : (comment?.photo ?? api?.photo)

        person.displayName = displayName
        person.givenName = givenName
        person.familyName = familyName
        person.email = email
        person.photo = photo
        person.initials = generateInitials(givenName, familyName, displayName, email)
        person.useInitialsAsAvatar = !photo
    }

    /**
     * Extract GPerson objects from Google People API response
     * @param response - Response from peopleGetBatchGet
     * @returns Array of GPerson objects
     */
    function extractPersons (response: gapi.client.people.GetPeopleResponse): GPerson[] {
        const persons: GPerson[] = []

        if (!response.responses) {
            return persons
        }

        for (const person of response.responses) {
            if (!person.person) {
                continue
            }

            const personData = person.person
            const resourceName = personData.resourceName || ''
            // Extract personId from resourceName (format: people/{id})
            const personId = resourceName.replace('people/', '') as ActorId

            if (!personId) {
                continue
            }

            const gPerson: GPerson = {
                personId,
                peopleApiPerson: extractPeopleApiPerson(personData),
                updated: Date.now(),
                useInitialsAsAvatar: true,
                bgColor: getRandomAvatarBgColor(),
            }

            if (import.meta.env.DEV) {
                gPerson.raw = JSON.stringify(personData)
            }

            updateProfile(gPerson)

            persons.push(gPerson)
        }

        return persons
    }

    /**
     * Augment cached GPerson records in Dexie with author info extracted from Drive comments.get().
     *
     * Records whose `commentAuthor` was already refreshed within the last hour are skipped.
     *
     * @param authorMap - Map of ActorId to author info extracted from comments/replies
     * @returns the ActorIds that were actually updated in Dexie
     */
    async function updatePeopleFromCommentAuthors (authorMap: Map<ActorId, GCommentDataAuthor>): Promise<ActorId[]> {
        const db = useDatabase()
        const ids = [...authorMap.keys()].filter(id => !isKnownActorId(id))

        if (ids.length === 0) {
            return []
        }

        const persons = await db.people.where('personId').anyOf(ids).toArray()
        const now = Date.now()
        const updatedPersons: GPerson[] = []

        for (const person of persons) {
            // no need to touch records whose commentAuthor was refreshed recently
            if (now - (person.commentAuthor?.updated ?? 0) < COMMENT_AUTHOR_REFRESH_INTERVAL) {
                continue
            }

            const authorData = authorMap.get(person.personId)
            if (!authorData) {
                continue
            }

            person.commentAuthor = {
                displayName: authorData.displayName || undefined,
                email: authorData.emailAddress || undefined,
                photo: authorData.photoLink || undefined,
                updated: now,
            }

            // derive `givenName` and `familyName` from `displayName` if available
            if (person.commentAuthor.displayName) {
                const parts = person.commentAuthor.displayName.split(' ')
                person.commentAuthor.givenName = parts[0]
                person.commentAuthor.familyName = parts.slice(1).join(' ') || undefined
            }

            updateProfile(person)
            person.updated = now
            updatedPersons.push(person)
        }

        if (updatedPersons.length > 0) {
            await db.people.bulkPut(updatedPersons.map(applyScreenshotsMode))
            useUIStore().markProfilesUpdated()
        }

        return updatedPersons.map(person => person.personId)
    }

    return {
        fetchNewPeople,
        fetchAllPersons,
        updatePeopleFromCommentAuthors,
    }
}
